import { useCallback, useContext } from 'react';
import LogRocket from 'logrocket';
import * as Sentry from '@sentry/react';
import { AppContext, ServerEnvs } from '../utils/AppContext';

const PRODUCTION_PROJECT_KEY = '8sdf2f/optibus-prod';
const DEV_PROJECT_KEY = '8sdf2f/optibus-dev';

const requestsToIgnore = ['mxpnl.com', '/assets/fonts'];
const requestsToIgnoreBody = ['/auth'];

const envsToEnable = ['titus'];

const shouldInitLogRocket = function shouldInitLogRocket(envData: ServerEnvs) {
  return envsToEnable.some((env) => envData?.ENV?.includes(env));
};

const getLogRocketToken = function getLogRocketToken(envData: any) {
  if (envData?.ENV && envData?.ENV.startWith('titus')) {
    return PRODUCTION_PROJECT_KEY; // Production
  }
  return DEV_PROJECT_KEY; // Development
};

const getRelease = function getRelease() {
  // TAG may look like push..os-26324-6..11..ac0333f79cbf74e37f6f85b303b07a83f312a20e
  return process.env?.REACT_APP_TAG ? process.env.REACT_APP_TAG.split('..').pop() : undefined;
};

const useLogRocket = () => {
  const { envData } = useContext(AppContext);

  const startLogRocket = useCallback(async () => {
    if (envData.ENV) {
      try {
        const shouldInit = shouldInitLogRocket(envData);
        if (shouldInit) {
          const token = getLogRocketToken(envData);
          LogRocket.init(token, {
            release: getRelease(),
            network: {
              requestSanitizer: (request) => {
                // If the url contains '/auth', we don't want to be able to see the request body (password).
                const requestUrl = request.url.toLowerCase();
                if (requestsToIgnore.some((urlPattern) => urlPattern.includes(requestUrl))) {
                  return null;
                }
                if (requestsToIgnoreBody.some((urlPattern) => urlPattern.includes(requestUrl))) {
                  request.body = undefined;
                }
                return request;
              },
            },
            rootHostname: 'optibus.co',
          });

          LogRocket.getSessionURL((sessionURL) => {
            Sentry.configureScope((scope) => {
              scope.setExtra('sessionURL', sessionURL);
            });
          });
        }
      } catch {
        // catch failed request and ignore it
      }
    }
  }, [envData]);

  return {
    startLogRocket,
  };
};

export default useLogRocket;
